import { TagProps, TooltipProps } from '~interfaces/invoice'
import { date } from '~utils/date'
import { money } from '~utils/money'
import { differenceInDays } from 'date-fns'
import { Invoice } from 'services/interfaces'
import { DataObjectProps } from '~containers/Signature/interfaces'

export const invoiceStatusDict: { [key: number]: string } = {
  2: 'Gerada',
  3: 'Atrasada',
  5: 'Paga',
}

export const invoicePaymentsStatus = {
  paid: 5,
  canceled: 4,
  generated: 2,
  overdue: 3,
}

const invoicePaymentsDayLimit = {
  available: 10,
  pending: 4,
}

const invoicePaymentsStatusText: {
  [key: number]: (date?: string) => TagProps
} = {
  3: () => ({
    label: `Fatura venceu`,
    color: 'red',
  }),
  5: () => ({
    label: 'Fatura paga',
    color: 'green',
  }),
}

const invoiceTooltipDescription = {
  available: (date: string) => ({
    label: 'Fatura disponível',
    content: `Sua fatura com vencimento no dia ${date} já está disponível para pagamento.`,
  }),
  pending: {
    label: 'Sua fatura vence em breve',
    content: 'Pague a fatura e evite o bloqueio de sua conta na Fretebras',
  },
  expired: {
    label: 'Sua fatura já venceu',
    content: 'O bloqueio de sua conta acontece 5 dias após o vencimento. Regularize agora para evitá-lo.',
  },
}

export const isInvoiceVisible = (invoiceData: DataObjectProps): boolean => {
  const currentDate = date(new Date().toISOString(), 'yyyy/MM/dd')

  const diffInDays = differenceInDays(new Date(invoiceData.raw_due_date), new Date(currentDate))

  return diffInDays <= invoicePaymentsDayLimit.available
}

export const isInvoicePending = (diff: number, status: number) =>
  status === invoicePaymentsStatus.generated && diff >= 0 && diff <= invoicePaymentsDayLimit.pending

export const isInvoiceAvailable = (diff: number, status: number) =>
  status === invoicePaymentsStatus.generated && diff > invoicePaymentsDayLimit.pending

export const isInvoiceExpired = (diff: number, status: number) =>
  (status === invoicePaymentsStatus.generated && diff < 0) || status === invoicePaymentsStatus.overdue

export const calculateDiffInDaysToInvoice = (d: string): number => {
  const currentDate = date(new Date().toISOString(), 'yyyy/MM/dd')

  return differenceInDays(new Date(d), new Date(currentDate))
}

export const getTooltipText = (invoiceData: DataObjectProps): TooltipProps | undefined => {
  const diffInDays = calculateDiffInDaysToInvoice(`${date(invoiceData.raw_due_date, 'yyyy/MM/dd')}`)

  if (isInvoicePending(diffInDays, invoiceData.status)) {
    return {
      ...invoiceTooltipDescription.pending,
    }
  }

  if (isInvoiceAvailable(diffInDays, invoiceData.status)) {
    return {
      ...invoiceTooltipDescription.available(invoiceData.due_date),
    }
  }

  if (isInvoiceExpired(diffInDays, invoiceData.status)) {
    return {
      ...invoiceTooltipDescription.expired,
    }
  }

  return undefined
}

export const mountTagProps = (invoiceData: DataObjectProps): TagProps => {
  const parsedDateInvoice = date(invoiceData.raw_due_date, "dd 'de' MMM")

  const diffInDays = calculateDiffInDaysToInvoice(`${date(invoiceData.raw_due_date, 'yyyy/MM/dd')}`)

  if (isInvoicePending(diffInDays, invoiceData.status)) {
    return {
      label: 'Vence em breve',
      color: 'orange',
    }
  }

  if (isInvoiceAvailable(diffInDays, invoiceData.status)) {
    return {
      label: 'Fatura disponível',
      color: undefined,
    }
  }

  if (isInvoiceExpired(diffInDays, invoiceData.status)) {
    return invoicePaymentsStatusText[3](parsedDateInvoice)
  }

  return invoicePaymentsStatusText[invoiceData.status]?.(parsedDateInvoice)
}

export const tableColumns = [
  { name: 'Serviço contratado', field: 'service' },
  { name: 'Vencimento', field: 'due_date' },
  { name: 'Pagamento', field: 'paid_date' },
  { name: 'Recorrência', field: 'recurrence' },
  { name: 'Valor', field: 'value' },
  { name: 'status', field: 'status', width: 180 },
  { name: '', field: 'actions' },
]

const mapperValidator = (
  hasData?: boolean,
  propertyExpected?: string | boolean | number,
  catchStatement?: null | string,
) => {
  return hasData ? propertyExpected : catchStatement
}

export const getCurrentInvoiceDataObject = (hasData: boolean, data: Invoice): DataObjectProps => ({
  loyal_flag: data?.data?.current?.invoice?.loyal_flag,
  billing: data?.data?.current?.invoice?.billing,
  amount_paid: hasData ? money(data?.data?.current?.payment?.amount_paid) : '',
  amount: hasData ? money(data?.data?.current?.payment?.amount) : '',
  raw_due_date: data?.data?.current?.invoice?.due_date,
  due_date: hasData ? date(data?.data?.current?.invoice?.due_date) : '',
  status: data?.data?.current?.invoice?.status,
  recurrence:
    hasData && data.data.current.invoice.recurrence
      ? data?.data?.current?.invoice?.recurrence.charAt(0).toUpperCase() +
        data?.data?.current?.invoice?.recurrence.slice(1)
      : '',
  plan_name: data?.data?.current?.combo?.nickname || data?.data?.current?.combo?.name,
  recurrence_start: hasData ? date(data?.data?.current?.invoice?.recurrence_start) : '',
  recurrence_end: hasData ? date(data?.data?.current?.invoice?.recurrence_end) : '',
  raw_discount: data?.data?.current?.payment?.discount,
  discount:
    hasData && data?.data?.current?.payment?.discount ? money(data?.data?.current?.payment?.discount) : money(0),
  products: hasData
    ? data?.data?.current?.combo?.products.map((p) => ({ name: p.name, description: p.description }))
    : [],
  url_invoice: data?.data?.current?.invoice?.link,
  url_payment: data?.data?.current?.payment?.link,
  kind_service: data?.data?.current?.invoice?.kind_service,
})

export const getCurrentDate = () => date(new Date().toISOString(), 'yyyy/MM/dd HH:mm')

export const getInvoiceLinkProps = (invoiceData: DataObjectProps) => {
  return {
    href: invoiceData?.url_payment,
    label: invoiceData?.status === invoicePaymentsStatus.paid ? 'Ver fatura' : 'Pagar fatura',
    target: '_blank' as const,
  }
}
